import { formatEther, formatUnits } from '@ethersproject/units';

import { getChainData } from './chain';
import {
  UNISWAP_SUBGRAPH_PATH,
  HONEYSWAP_SUBGRAPH_PATH,
  QUICKSWAP_SUBGRAPH_PATH,
  POLYGON_TREASURY_ACCOUNT,
} from './constants';
import {
  pancakeSwapContract,
  polywaveContract,
  rSurfTokenContract,
  surf3dContract,
  towelStakeContract,
  towelTokenContract,
  waveTokenContract,
  whirlpoolContract,
} from './contracts';
import {
  ChainId,
  Currency,
  CurrencyBalance,
  DappBalance,
  ExchangeGraphResponse,
  Pair,
  PriceMapping,
  Token,
} from './types';

export const parseEther = (n: number) => parseFloat(formatEther(n));

export const getEthWalletBalance = async (account: string): Promise<CurrencyBalance> => {
  const balance = {
    [Currency.SURF]: 0,
  };
  const { surfTokenContract } = getChainData(ChainId.EthMainnet);
  try {
    const result = await surfTokenContract.balanceOf(account);
    balance[Currency.SURF] = parseEther(result);
  } catch (err) {
    console.error('Error fetching Ethereum wallet balance', err);
  }

  return balance;
};

export const getXDaiWalletBalance = async (account: string): Promise<CurrencyBalance> => {
  const balance = {
    [Currency.SURF]: 0,
    [Currency.RSURF]: 0,
  };
  const { surfTokenContract } = getChainData(ChainId.XDai);
  try {
    const [surfResult, rSurfResult] = await Promise.all([
      surfTokenContract.balanceOf(account),
      rSurfTokenContract.balanceOf(account),
    ]);
    balance[Currency.SURF] = parseEther(surfResult);
    balance[Currency.RSURF] = rSurfResult / 1e9;
  } catch (err) {
    console.error('Error fetching xDai wallet balance', err);
  }

  return balance;
};

export const getBscWalletBalance = async (account: string): Promise<CurrencyBalance> => {
  const balance = {
    [Currency.SURF]: 0,
  };
  const { surfTokenContract } = getChainData(ChainId.BscMainnet);
  try {
    const result = await surfTokenContract.balanceOf(account);
    balance[Currency.SURF] = parseEther(result);
  } catch (err) {
    console.error('Error fetching BSC wallet balance', err);
  }

  return balance;
};

export const getMaticWalletBalance = async (account: string): Promise<CurrencyBalance> => {
  const balance = {
    [Currency.SURF]: 0,
    [Currency.TOWEL]: 0,
    [Currency.WAVE]: 0,
  };
  const { surfTokenContract } = getChainData(ChainId.MaticMainnet);
  try {
    const [surfResult, towelResult, waveResult] = await Promise.all([
      surfTokenContract.balanceOf(account),
      towelTokenContract.balanceOf(account),
      waveTokenContract.balanceOf(account),
    ]);
    balance[Currency.SURF] = parseEther(surfResult);
    balance[Currency.TOWEL] = parseEther(towelResult);
    balance[Currency.WAVE] = parseEther(waveResult);
  } catch (err) {
    console.error('Error fetching Polygon wallet balance', err);
  }

  return balance;
};

export const getSurf3dBalance = async (account: string): Promise<DappBalance> => {
  const balance = {
    locked: {
      [Currency.SURF]: 0,
    },
    unclaimed: {
      [Currency.SURF]: 0,
    },
  };
  try {
    const { userLiquidValue, userDividends } = await surf3dContract.allInfoFor(account);
    balance.locked[Currency.SURF] = parseEther(userLiquidValue);
    balance.unclaimed[Currency.SURF] = parseEther(userDividends);
  } catch (err) {
    console.error('Error fetching Surf3d balance', err);
  }

  return balance;
};

export const getWhirlpoolBalance = async (account: string): Promise<DappBalance> => {
  const balance = {
    locked: {
      [Currency.ETH_SURF_LP]: 0,
    },
    unclaimed: {
      [Currency.SURF]: 0,
    },
  };
  try {
    const { info } = await whirlpoolContract.getAllInfoFor(account);
    balance.locked[Currency.ETH_SURF_LP] = parseEther(info[9]);
    balance.unclaimed[Currency.SURF] = parseEther(info[10]);
  } catch (err) {
    console.error('Error fetching Whirlpool balance', err);
  }

  return balance;
};

export const getBeachBalance = async (account: string): Promise<DappBalance> => {
  const balance = {
    locked: {
      [Currency.SURF]: 0,
      [Currency.TOWEL]: 0,
    },
    unclaimed: {
      [Currency.SURF]: 0,
    },
  };
  try {
    const [{ userDividends, userStaked }, { userLocked }] = await Promise.all([
      towelStakeContract.allInfoFor(account),
      towelTokenContract.allInfoFor(account),
    ]);
    balance.locked[Currency.SURF] = parseEther(userLocked);
    balance.locked[Currency.TOWEL] = parseEther(userStaked);
    balance.unclaimed[Currency.SURF] = parseEther(userDividends);
  } catch (err) {
    console.error('Error fetching Beach balance', err);
  }

  return balance;
};

export const getPolywaveBalance = async (account: string): Promise<DappBalance> => {
  const balance = {
    locked: {
      [Currency.WAVE]: 0,
      [Currency.WAVE_MATIC_LP]: 0,
      [Currency.SURF]: 0,
      [Currency.TOWEL]: 0,
    },
    unclaimed: {
      [Currency.WAVE]: 0,
    },
  };
  try {
    const { compressedInfos } = await polywaveContract.allInfoFor(account);
    const [waveInfo, waveMaticLpInfo, surfInfo, towelInfo] = compressedInfos;
    balance.locked[Currency.WAVE] = parseEther(waveInfo[7]);
    balance.locked[Currency.WAVE_MATIC_LP] = parseEther(waveMaticLpInfo[7]);
    balance.locked[Currency.SURF] = parseEther(surfInfo[7]);
    balance.locked[Currency.TOWEL] = parseEther(towelInfo[7]);
    compressedInfos.forEach((info: any) => {
      balance.unclaimed[Currency.WAVE] += parseEther(info[8]);
    });
  } catch (err) {
    console.error('Error fetching polyWAVE balance', err);
  }

  return balance;
};

export const getPolygonTreasuryBalance = async (): Promise<[CurrencyBalance, CurrencyBalance]> => {
  const currencies = [
    Currency.WAVE,
    Currency.WAVE_MATIC_LP,
    Currency.SURF,
    Currency.TOWEL,
    Currency.USDC,
    Currency.USDT,
    Currency.DAI,
    Currency.WMATIC,
    Currency.WETH,
    Currency.WBTC,
    Currency.UNI,
    Currency.QUICK,
    Currency.AAVE,
    Currency.LINK,
    Currency.PCOMB,
    Currency.LMAO,
    Currency.TITAN,
    Currency.WRBT,
    Currency.WLEV,
    Currency.AGAR,
  ];
  const { compressedInfos } = await polywaveContract.allInfoFor(POLYGON_TREASURY_ACCOUNT);
  const balance: CurrencyBalance = {};
  const balanceMatic: CurrencyBalance = {};
  compressedInfos.forEach((info: any, i: number) => {
    const currency = currencies[i];
    if (currency !== Currency.WAVE_MATIC_LP) {
      const count = parseFloat(formatUnits(info[9], info[6]));
      balance[currency] = count;
      balanceMatic[currency] = parseEther(info[5]) * count;
    }
  });

  return [balance, balanceMatic];
};

export const mergeCurrencyBalances = (balances: CurrencyBalance[]): CurrencyBalance => {
  const totalBalance: CurrencyBalance = {};
  balances.forEach((balance) => {
    (Object.keys(balance) as Array<Currency>).forEach((currency) => {
      totalBalance[currency] = (totalBalance[currency] || 0) + balance[currency]!;
    });
  });

  return totalBalance;
};

export const convertCurrencyBalance = (
  balance: CurrencyBalance,
  tokenPrices: PriceMapping,
  quoteCurrency: Currency
): CurrencyBalance => {
  const newBalance: CurrencyBalance = {};
  (Object.keys(balance) as Array<Currency>).forEach((currency) => {
    newBalance[currency] = (tokenPrices[currency]?.[quoteCurrency] || 0) * balance[currency]!;
  });

  return newBalance;
};

export const sumCurrencyBalance = (balance: CurrencyBalance): number => {
  let sum = 0;
  (Object.keys(balance) as Array<Currency>).forEach((currency) => {
    sum += balance[currency]!;
  });

  return sum;
};

export const mergeDappBalances = (balances: DappBalance[]): DappBalance => {
  const totalBalance: DappBalance = {
    locked: {},
    unclaimed: {},
  };
  balances.forEach((balance) => {
    (Object.keys(balance.locked) as Array<Currency>).forEach((currency) => {
      totalBalance.locked[currency] =
        (totalBalance.locked[currency] || 0) + balance.locked[currency]!;
    });
    (Object.keys(balance.unclaimed) as Array<Currency>).forEach((currency) => {
      totalBalance.unclaimed[currency] =
        (totalBalance.unclaimed[currency] || 0) + balance.unclaimed[currency]!;
    });
  });

  return totalBalance;
};

export const formatUsdPrice = (price: number) => `$${price.toPrecision(4)}`;

export const formatBalance = (balance: number) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  });

  return formatter.format(balance);
};

export const getEthTokenPrices = async (): Promise<PriceMapping> => {
  const tokenPrices = {
    [Currency.ETH]: {
      [Currency.ETH]: 1,
      [Currency.USD]: 0,
    },
    [Currency.SURF]: {
      [Currency.SURF]: 1,
      [Currency.ETH]: 0,
      [Currency.USD]: 0,
    },
    [Currency.WLEV]: {
      [Currency.WLEV]: 1,
      [Currency.ETH]: 0,
      [Currency.USD]: 0,
      [Currency.SURF]: 0,
    },
    [Currency.ETH_SURF_LP]: {
      [Currency.ETH_SURF_LP]: 1,
      [Currency.ETH]: 0,
      [Currency.USD]: 0,
    },
  };
  const query = `{
    pairs(where: { id_in: ["${Pair.ETH_SURF}", "${Pair.WLEV_ETH}"] }) {
      id
      token0Price
      token1Price
      reserveETH
      reserveUSD
      totalSupply
    }
    bundle(id: "1") {
      ethPrice
    }
  }`;
  const response = await fetch(UNISWAP_SUBGRAPH_PATH, {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
  const { data } = await response.json();
  const { bundle, pairs } = data as ExchangeGraphResponse;
  pairs.forEach(({ id, token0Price, token1Price, reserveETH, reserveUSD, totalSupply }) => {
    switch (id) {
      case Pair.ETH_SURF:
        tokenPrices[Currency.SURF][Currency.ETH] = parseFloat(token0Price);
        if (reserveETH && reserveUSD && totalSupply) {
          const parsedTotalSupply = parseFloat(totalSupply);
          tokenPrices[Currency.ETH_SURF_LP][Currency.ETH] =
            parseFloat(reserveETH) / parsedTotalSupply;
          tokenPrices[Currency.ETH_SURF_LP][Currency.USD] =
            parseFloat(reserveUSD) / parsedTotalSupply;
        }
        break;
      case Pair.WLEV_ETH:
        tokenPrices[Currency.WLEV]![Currency.ETH] = parseFloat(token1Price);
        break;
    }
  });

  const ethUsd = parseFloat(bundle!.ethPrice);
  const surfEth = tokenPrices[Currency.SURF][Currency.ETH];
  const wlevEth = tokenPrices[Currency.WLEV][Currency.ETH];
  tokenPrices[Currency.ETH][Currency.USD] = ethUsd;
  tokenPrices[Currency.SURF][Currency.USD] = surfEth * ethUsd;
  tokenPrices[Currency.WLEV][Currency.USD] = wlevEth * ethUsd;
  tokenPrices[Currency.WLEV][Currency.SURF] = (1 / surfEth) * wlevEth;
  return tokenPrices;
};

export const getXDaiTokenPrices = async (): Promise<PriceMapping> => {
  const tokenPrices = {
    [Currency.SURF]: {
      [Currency.SURF]: 1,
      [Currency.USD]: 0,
    },
    [Currency.RSURF]: {
      [Currency.RSURF]: 1,
      [Currency.USD]: 0,
      [Currency.SURF]: 0,
    },
    [Currency.WLEV]: {
      [Currency.WLEV]: 1,
      [Currency.USD]: 0,
      [Currency.RSURF]: 0,
      [Currency.SURF]: 0,
    },
  };
  const query = `{
    pairs(where: { id_in: ["${Pair.SURF_WXDAI}", "${Pair.SURF_RSURF}", "${Pair.RSURF_WXDAI}", "${Pair.RSURF_WLEV}"] }) {
      id
      token0Price
      token1Price
    }
  }`;
  const response = await fetch(HONEYSWAP_SUBGRAPH_PATH, {
    method: 'POST',
    body: JSON.stringify({ query }),
  });
  const { data } = await response.json();
  const { pairs } = data as ExchangeGraphResponse;
  pairs.forEach(({ id, token0Price, token1Price }) => {
    const basePrice = parseFloat(token0Price);
    const quotePrice = parseFloat(token1Price);
    switch (id) {
      case Pair.SURF_WXDAI:
        tokenPrices[Currency.SURF][Currency.USD] = quotePrice;
        break;
      case Pair.SURF_RSURF:
        tokenPrices[Currency.RSURF][Currency.SURF] = basePrice;
        break;
      case Pair.RSURF_WXDAI:
        tokenPrices[Currency.RSURF][Currency.USD] = quotePrice;
        break;
      case Pair.RSURF_WLEV:
        tokenPrices[Currency.WLEV][Currency.RSURF] = basePrice;
        break;
    }
  });

  const wlevRSruf = tokenPrices[Currency.WLEV][Currency.RSURF];
  tokenPrices[Currency.WLEV][Currency.USD] = wlevRSruf * tokenPrices[Currency.RSURF][Currency.USD];
  tokenPrices[Currency.WLEV][Currency.SURF] =
    wlevRSruf * tokenPrices[Currency.RSURF][Currency.SURF];
  return tokenPrices;
};

export const getBscTokenPrices = async (): Promise<PriceMapping> => {
  const [[surfUsdResult], [bnbUsdResult]] = await Promise.all([
    pancakeSwapContract.getAmountsIn(1e15, [Token.BSC_DAI, Token.WBNB, Token.BSC_SURF]),
    pancakeSwapContract.getAmountsIn(1e15, [Token.BSC_DAI, Token.WBNB]),
  ]);
  const surfUsd = surfUsdResult * 1e-15;
  const bnbUsd = bnbUsdResult * 1e-15;
  return {
    [Currency.BNB]: {
      [Currency.BNB]: 1,
      [Currency.USD]: bnbUsd,
    },
    [Currency.SURF]: {
      [Currency.SURF]: 1,
      [Currency.USD]: surfUsd,
      [Currency.BNB]: surfUsd / bnbUsd,
    },
  };
};

export const getMaticTokenPrices = async (): Promise<PriceMapping> => {
  const tokenPrices = {
    [Currency.ETH]: {
      [Currency.ETH]: 1,
      [Currency.USD]: 0,
    },
    [Currency.MATIC]: {
      [Currency.MATIC]: 1,
      [Currency.USD]: 0,
      [Currency.ETH]: 0,
    },
    [Currency.SURF]: {
      [Currency.SURF]: 1,
      [Currency.USD]: 0,
      [Currency.ETH]: 0,
      [Currency.MATIC]: 0,
    },
    [Currency.TOWEL]: {
      [Currency.TOWEL]: 1,
      [Currency.USD]: 0,
      [Currency.SURF]: 0,
      [Currency.ETH]: 0,
      [Currency.MATIC]: 0,
    },
    [Currency.WAVE]: {
      [Currency.WAVE]: 1,
      [Currency.USD]: 0,
      [Currency.SURF]: 0,
      [Currency.ETH]: 0,
      [Currency.MATIC]: 0,
    },
    [Currency.WAVE_MATIC_LP]: {
      [Currency.WAVE_MATIC_LP]: 1,
      [Currency.USD]: 0,
      [Currency.MATIC]: 0,
      [Currency.WAVE]: 0,
    },
  };
  const query = `{
    pairs(where: { id_in: ["${Pair.WMATIC_USDC}", "${Pair.WMATIC_WETH}", "${Pair.SURF_WETH}"] }) {
      id
      token1Price
    },
    bundle(id: "1") {
      ethPrice
    }
  }`;
  const [
    quickswapResponse,
    {
      compressedInfos: [waveInfo, waveMaticLpInfo, surfInfo, towelInfo],
    },
  ] = await Promise.all([
    fetch(QUICKSWAP_SUBGRAPH_PATH, {
      method: 'POST',
      body: JSON.stringify({ query }),
    }),
    polywaveContract.allInfoFor(POLYGON_TREASURY_ACCOUNT),
  ]);
  const { data } = await quickswapResponse.json();
  const { pairs, bundle } = data as ExchangeGraphResponse;
  pairs.forEach(({ id, token1Price }) => {
    const quotePrice = parseFloat(token1Price);
    switch (id) {
      case Pair.WMATIC_USDC:
        tokenPrices[Currency.MATIC][Currency.USD] = quotePrice;
        break;
      case Pair.WMATIC_WETH:
        tokenPrices[Currency.MATIC][Currency.ETH] = quotePrice;
        break;
      case Pair.SURF_WETH:
        tokenPrices[Currency.SURF][Currency.ETH] = quotePrice;
        break;
    }
  });

  const ethUsd = parseFloat(bundle!.ethPrice);
  const maticEth = tokenPrices[Currency.MATIC][Currency.ETH];
  const maticUsd = tokenPrices[Currency.MATIC][Currency.USD];
  const surfMatic = parseEther(surfInfo[5]);
  const towelMatic = parseEther(towelInfo[5]);
  const waveMatic = parseEther(waveInfo[5]);
  const waveMaticLpMatic = parseEther(waveMaticLpInfo[5]);
  tokenPrices[Currency.ETH][Currency.USD] = ethUsd;
  tokenPrices[Currency.SURF][Currency.USD] = surfMatic * maticUsd;
  tokenPrices[Currency.SURF][Currency.MATIC] = surfMatic;
  tokenPrices[Currency.TOWEL][Currency.MATIC] = towelMatic;
  tokenPrices[Currency.TOWEL][Currency.USD] = towelMatic * maticUsd;
  tokenPrices[Currency.TOWEL][Currency.SURF] = towelMatic / surfMatic;
  tokenPrices[Currency.TOWEL][Currency.ETH] = towelMatic * maticEth;
  tokenPrices[Currency.WAVE][Currency.MATIC] = waveMatic;
  tokenPrices[Currency.WAVE][Currency.USD] = waveMatic * maticUsd;
  tokenPrices[Currency.WAVE][Currency.SURF] = waveMatic / surfMatic;
  tokenPrices[Currency.WAVE][Currency.ETH] = waveMatic * maticEth;
  tokenPrices[Currency.WAVE_MATIC_LP][Currency.MATIC] = waveMaticLpMatic;
  tokenPrices[Currency.WAVE_MATIC_LP][Currency.USD] = waveMaticLpMatic * maticUsd;
  tokenPrices[Currency.WAVE_MATIC_LP][Currency.WAVE] = waveMaticLpMatic / waveMatic;
  return tokenPrices;
};
